/**
 * ShopProductsView - Products list view for Shop administration
 * Extracted from AdminShopTab.js in Phase 4D-8
 * 
 * Displays:
 * - Search and filter controls (category, status)
 * - Products table with image, title, SKU, variants, category, price, stock, status
 * - Action buttons (visibility toggle, edit, delete)
 * - Empty state when no products
 * 
 * Product Filtering:
 * - Search by title or SKU
 * - Filter by category
 * - Filter by status (active/hidden)
 */
import React from 'react';
import { Package, Search, Plus, Pencil, Trash2, Eye, EyeOff, Image } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { formatCurrency } from '../../../utils/format';

// Category options for filtering
const CATEGORY_OPTIONS = ['Clothing', 'Accessories', 'Dance Shoes', 'Music', 'Gifts', 'Other'];

/**
 * Calculate total stock across all variants or base stock
 * @param {Object} product - Product object
 * @returns {number} Total stock quantity
 */
function getTotalStock(product) {
  if (product.variants?.length > 0) {
    return product.variants.reduce((sum, v) => sum + (v.stock_quantity || 0), 0);
  }
  return product.stock_quantity || 0;
}

/**
 * @param {Object} props
 * @param {Array} props.products - Array of product objects
 * @param {string} props.searchQuery - Current search text
 * @param {Function} props.onSearchChange - Callback when search changes
 * @param {string} props.categoryFilter - Current category filter ('all' or category name)
 * @param {Function} props.onCategoryChange - Callback when category filter changes
 * @param {string} props.statusFilter - Current status filter ('all', 'active', 'hidden')
 * @param {Function} props.onStatusChange - Callback when status filter changes
 * @param {Function} props.onAddProduct - Callback when Add Product button clicked
 * @param {Function} props.onEditProduct - Callback when edit button clicked (receives product)
 * @param {Function} props.onToggleActive - Callback when visibility toggle clicked (receives product)
 * @param {Function} props.onDeleteProduct - Callback when delete button clicked (receives product)
 * @param {string} props.apiUrl - API base URL for image paths
 */
export function ShopProductsView({
  products,
  searchQuery,
  onSearchChange,
  categoryFilter,
  onCategoryChange,
  statusFilter,
  onStatusChange,
  onAddProduct,
  onEditProduct,
  onToggleActive,
  onDeleteProduct,
  apiUrl
}) {
  // Apply filters to products
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || 
                          (statusFilter === 'active' && p.is_active) ||
                          (statusFilter === 'hidden' && !p.is_active);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle>Products ({filteredProducts.length})</CardTitle>
            <CardDescription>Manage your product catalog</CardDescription>
          </div>
          <Button onClick={onAddProduct} data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10"
              data-testid="product-search"
            />
          </div>
          <Select value={categoryFilter} onValueChange={onCategoryChange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORY_OPTIONS.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={onStatusChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Products Table */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No products found</p>
            <Button onClick={onAddProduct} variant="outline" className="mt-4">
              Add Your First Product
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Image</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.product_id} data-testid={`product-row-${product.product_id}`}>
                    <TableCell>
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0].startsWith('/') ? `${apiUrl}${product.images[0]}` : product.images[0]}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                          <Image className="h-5 w-5 text-slate-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{product.title}</p>
                        {product.sku && (
                          <p className="text-xs text-slate-500">SKU: {product.sku}</p>
                        )}
                        {product.variants?.length > 0 && (
                          <p className="text-xs text-slate-500">{product.variants.length} variants</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {product.category ? (
                        <Badge variant="secondary">{product.category}</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(product.price)}
                      {product.compare_at_price && (
                        <span className="text-xs text-slate-400 line-through ml-2">
                          {formatCurrency(product.compare_at_price)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {product.track_inventory ? (
                        <span className={getTotalStock(product) <= 5 ? 'text-red-600 font-medium' : ''}>
                          {getTotalStock(product)}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={product.is_active ? 'default' : 'secondary'}>
                        {product.is_active ? 'Active' : 'Hidden'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onToggleActive(product)}
                          title={product.is_active ? 'Hide product' : 'Show product'}
                        >
                          {product.is_active ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditProduct(product)}
                          data-testid={`edit-product-${product.product_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteProduct(product)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ShopProductsView;
